/**
 * TanStack Query hooks for BYOK AI provider key management (AB#669).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiKeysApi } from '../api/aiKeys';
import type { CreateAiProviderKeyRequest, UpdateAiProviderKeyRequest } from '../api/aiKeys';

// ============================================================================
// Query Keys
// ============================================================================

export const aiKeyKeys = {
  all: ['ai-keys'] as const,
  list: (siteSlug?: string) => [...aiKeyKeys.all, 'list', siteSlug ?? ''] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/** Visible masked key rows, optionally scoped to one site's shared keys. */
export function useAiKeys(siteSlug?: string) {
  return useQuery({
    queryKey: aiKeyKeys.list(siteSlug),
    queryFn: () => aiKeysApi.list(siteSlug),
  });
}

/** Create a BYOK key. */
export function useCreateAiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAiProviderKeyRequest) => aiKeysApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeyKeys.all });
    },
  });
}

/** Update a key's label / is_shared, or replace its secret. */
export function useUpdateAiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pid, ...data }: UpdateAiProviderKeyRequest & { pid: string }) =>
      aiKeysApi.update(pid, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeyKeys.all });
    },
  });
}

/** Delete a key. */
export function useDeleteAiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pid: string) => aiKeysApi.delete(pid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeyKeys.all });
    },
  });
}
